import React, { useCallback, useMemo, useState } from 'react';
import { prop } from 'ramda';
import { publicEventsAPIService } from '@core/js/services/publicEventsAPI.service';
import { useSetCurrentLanguage } from '@theme/js/hooks/language';
import { useTranslations } from '@core/js/hooks/i18n';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';

import style from './Language.scss';

export const Language: React.FC = () => {
    /* Lugano changes */
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const translate = useTranslations();
    const currentLanguage = useUserSelector(prop('lang'));
    const allowedLocales = useAppSelector(prop('allowedLocales'));
    const setCurrentLanguage = useSetCurrentLanguage();

    const filteredLocales = useMemo(
        () => allowedLocales.filter((locale) => locale !== currentLanguage),
        [allowedLocales, currentLanguage]
    );

    const toggleDropdown = useCallback(() => {
        setDropdownOpen((prev) => !prev);
    }, []);

    const closeDropdown = useCallback(() => {
        setDropdownOpen(false);
    }, []);

    const handleLanguageSelect = useCallback((language: string): void => {
            closeDropdown();
            setCurrentLanguage(language, true, true);
            publicEventsAPIService.trigger('user:action:changeLanguage', language);
        }, [setCurrentLanguage, closeDropdown]);

    return (
        <div className={`${style.language} ${isDropdownOpen ? style.isDropdownOpen : ''}`}>
            {isDropdownOpen && (
                <div
                    className={style.toggleOverlay}
                    onClick={closeDropdown}
                    aria-hidden={true}
                />
            )}

            <div
                className={style.toggleSelectLanguage}
                onClick={toggleDropdown}
                role="button"
                tabIndex={0}
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
            >
                <span className={style.currentLang}>
                    <span className={`${style.iconWorld} icon-ico-s4w-world`} aria-hidden={true} />
                    <span className={style.currentLangName}>
                        {translate(`playtech.mobile.system.language.${currentLanguage}`)}
                    </span>
                </span>
                <span
                    className={`${style.iconArrow} ${isDropdownOpen ? style.iconArrowBottom : ''} icon-ico-s4w-arrow-top`}
                    aria-hidden={true}
                />
            </div>

            {isDropdownOpen && (
                <ul
                    className={style.toggleSelectList}
                    role="listbox"
                    aria-activedescendant={currentLanguage}
                >
                    {filteredLocales.map((lang) => (
                        <li
                            key={lang}
                            className={style.langItem}
                            onClick={() => handleLanguageSelect(lang)}
                            role="option"
                            aria-selected={lang === currentLanguage}
                            tabIndex={0}
                        >
                            {translate(`playtech.mobile.system.language.${lang}`)}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
    /* Lugano changes */
};
